/**
 * Centralises the per-domain fetch loop for the dynamic exception
 * route. Replaces the previous 6-branch domain switch inside the route
 * with a single dispatcher table keyed by `data_domain`, plus a
 * config-supplied `buildFetchParams` callback that owns the per-page
 * URL-to-params translation.
 *
 * Returns a stable `refetch` so callers can re-run the fetch on demand
 * (e.g. after writes from a row-detail dialog), though that is not
 * wired up today.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import inspectorApi from '../services/inspector/api'
import type {
  ExceptionConfig,
  ExceptionUrlState,
} from '../config/exceptions/types'

interface UseExceptionDataFetchOptions {
  fpCode: string | undefined
  exceptionCode: string | undefined
  dataDomain: string | undefined
  config: ExceptionConfig | null
  urlState: ExceptionUrlState
  /**
   * Lifted filter-panel values (multiselect / select / toggle / daterange /
   * search), surfaced into `buildFetchParams` so configs can translate
   * panel filters into server-side query params. Changing this object
   * re-fires the fetch effect.
   */
  filterValues?: Record<string, unknown>
  /**
   * If true, skip the fetch entirely (e.g. while a legacy-redirect
   * navigation is in flight or before the exception type has loaded).
   */
  skip?: boolean
}

interface UseExceptionDataFetchReturn {
  data: Record<string, any>[]
  response: Record<string, any> | null
  loading: boolean
  error: string | null
  refetch: () => void
}

// Maps the canonical `data_domain` field on the exception type record
// to the corresponding inspectorApi method. Keeping this here means
// adding a new domain is one map entry, not a new branch in the route.
type DomainFetcher = (
  fpCode: string,
  exceptionCode: string,
  params?: Record<string, any>,
) => Promise<Record<string, any>>

const DOMAIN_FETCHERS: Record<string, DomainFetcher> = {
  sale: (fp, code, params) =>
    inspectorApi.getSalesExceptions(fp, code, params as any),
  stock_management: (fp, code, params) =>
    inspectorApi.getStockManagementExceptions(fp, code, params as any),
  product_setup: (fp, code, params) =>
    inspectorApi.getProductSetupExceptions(fp, code, params as any),
  product_pricing: (fp, code, params) =>
    inspectorApi.getProductPricingExceptions(fp, code, params as any),
  system: (fp, code, params) =>
    inspectorApi.getSystemExceptions(fp, code, params as any),
  client: (fp, code, params) =>
    inspectorApi.getClientExceptions(fp, code, params as any),
  accounting: (fp, code, params) =>
    inspectorApi.getAccountingExceptions(fp, code, params as any),
}

// Response envelope keys vary across domains: sales -> `sales`,
// inventory domains -> `inventory`, systems -> `systems`, client ->
// `client`, accounting -> `data` (or `sales` for the COGS-monthly
// special-case). The legacy route used a chain of fallbacks; preserve it.
const extractItems = (
  response: Record<string, any> | null,
  dataDomain: string | undefined,
): Record<string, any>[] => {
  if (!response) return []
  const envelopeKey =
    dataDomain === 'sale'
      ? 'sales'
      : dataDomain === 'system'
        ? 'systems'
        : dataDomain === 'client'
          ? 'client'
          : dataDomain === 'accounting'
            ? 'data'
            : 'inventory'
  return (
    (response[envelopeKey] as Record<string, any>[] | undefined) ||
    // Accounting COGS-monthly special-case keeps the legacy `sales` envelope.
    (response.sales as Record<string, any>[] | undefined) ||
    (response.inventory as Record<string, any>[] | undefined) ||
    (response.data as Record<string, any>[] | undefined) ||
    []
  )
}

/**
 * Always-applied baseline params for every per-domain list fetch.
 *
 * Covers the cross-cutting axes that every exception view needs to
 * forward to the backend: lifecycle ``status`` from the URL, server-table
 * paging (``limit`` / ``offset``), and ``search`` when the config opts in.
 *
 * Kept separate from ``config.buildFetchParams`` so a config's override
 * is *additive*, not total - one place to read & write the baseline,
 * and a missing pickup in a config can't silently break lifecycle
 * filtering (the original ``?status=closed`` bug on the product
 * exceptions came from a per-config override that forgot to forward
 * ``status``).
 */
const buildBaselineFetchParams = (
  config: ExceptionConfig | null,
  urlState: ExceptionUrlState,
): Record<string, any> => {
  const params: Record<string, any> = {}
  const status = urlState.searchParams.get('status')
  if (status) params.status = status
  if (!config?.serverTable) return params
  params.limit = urlState.pageSize
  params.offset = urlState.page * urlState.pageSize
  if ((config.serverTable.enableSearch ?? true) && urlState.search) {
    params.search = urlState.search
  }
  return params
}

export function useExceptionDataFetch({
  fpCode,
  exceptionCode,
  dataDomain,
  config,
  urlState,
  filterValues,
  skip,
}: UseExceptionDataFetchOptions): UseExceptionDataFetchReturn {
  const [data, setData] = useState<Record<string, any>[]>([])
  const [response, setResponse] = useState<Record<string, any> | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  // Generation token: every fetch invocation captures the current value,
  // and only the latest invocation is allowed to write state. Guards
  // against (a) the effect re-firing on rapid param changes (page 1 ->
  // 2 -> 3) and a slow earlier response overwriting a fast later one,
  // and (b) a manual refetch racing an in-flight effect-driven run.
  const generationRef = useRef(0)

  const run = useCallback(async () => {
    if (skip || !fpCode || !exceptionCode || !dataDomain || !config) return
    // A config-level override wins over the per-domain dispatcher - used
    // by codes whose canonical list lives on a bespoke endpoint (e.g. the
    // grouped duplicate-sales list).
    const fetcher: DomainFetcher = config.fetchOverride
      ? (fp, _code, params) => config.fetchOverride!(fp, params)
      : DOMAIN_FETCHERS[dataDomain]
    if (!fetcher) {
      console.error(`No fetcher registered for data domain: ${dataDomain}`)
      return
    }
    const myGeneration = ++generationRef.current
    setLoading(true)
    setError(null)
    try {
      const stateWithFilters: ExceptionUrlState = {
        ...urlState,
        filterValues: filterValues ?? urlState.filterValues,
      }
      // Baseline (status / paging / search) is always applied; the
      // per-config override returns only the config-specific extras.
      // Extras win on key collision so a config can intentionally
      // shape a baseline param (e.g. compareExceptions remapping
      // ``search`` for its own server-side filter).
      const baseParams = buildBaselineFetchParams(config, stateWithFilters)
      const extras = config.buildFetchParams?.(stateWithFilters) ?? {}
      const params = { ...baseParams, ...extras }
      const res = await fetcher(
        fpCode,
        exceptionCode,
        Object.keys(params).length > 0 ? params : undefined,
      )
      if (myGeneration !== generationRef.current) return
      if (res) {
        setResponse(res)
        setData(extractItems(res, dataDomain))
      }
    } catch (err: any) {
      if (myGeneration !== generationRef.current) return
      console.error('Failed to fetch exception data:', err)
      setError(err?.message || 'Failed to load exception data')
    } finally {
      if (myGeneration === generationRef.current) setLoading(false)
    }
    // buildParams is rebuilt every render when buildFetchParams is
    // omitted from config; including it would re-fire the effect on
    // every render. We depend on urlState (which changes when params
    // change) instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, fpCode, exceptionCode, dataDomain, config, urlState, filterValues])

  useEffect(() => {
    run()
    // Bumping the generation on cleanup invalidates any in-flight write
    // from this effect run, so the next effect's results land cleanly.
    return () => {
      generationRef.current++
    }
  }, [run])

  return { data, response, loading, error, refetch: run }
}
